import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import { motion } from "framer-motion";
import RescheduleModal from "../Leads_V2/Followup/RescheduleModal";
import { rescheduleLeadFollowupApi, markFollowupCompletedApi } from "../../api/leads.api";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);
const MotionBox = motion.create ? motion.create(Box) : motion(Box);

export default function NextFollowUps({ followup, onUpdateFollowup, leadId, lead, followupStatus, refreshLead }) {
  const [value, setValue] = useState(followup ? dayjs(followup) : null);
  const [open, setOpen] = useState(false);
  const [rescheduleInfo, setRescheduleInfo] = useState(null);
  const theme = useTheme();

  // Initialize reschedule info from lead's reschedule history
  useEffect(() => {
    if (lead?.reschedule_history && lead.reschedule_history.length > 0) {
      const latestReschedule = lead.reschedule_history[lead.reschedule_history.length - 1];
      setRescheduleInfo({
        reason: latestReschedule.reason,
        notes: latestReschedule.notes,
        time: latestReschedule.preferred_time,
        rescheduled_at: latestReschedule.rescheduled_at,
        priority: latestReschedule.priority,
      });
    }
  }, [lead]);

  // Get the status display config
  const getStatusConfig = () => {
    switch (followupStatus) {
      case "completed":
        return {
          label: "Completed",
          bgColor: "#D1FAE5",
          color: "#059669",
          showRescheduleInfo: false,
        };
      case "rescheduled":
        return {
          label: "Rescheduled",
          bgColor: "#EDE9FE",
          color: "#8B5CF6",
          showRescheduleInfo: true,
        };
      case "skipped":
        return {
          label: "Skipped",
          bgColor: "#FEE2E2",
          color: "#DC2626",
          showRescheduleInfo: false,
        };
      default: {
        // For pending — show overdue, due today, or upcoming based on date
        if (!followup) return { label: null, showRescheduleInfo: false };

        const now = new Date();
        const dueDate = new Date(followup);
        const todayStr = now.toLocaleDateString("en-CA");
        const dueStr = dueDate.toLocaleDateString("en-CA");

        if (dueStr === todayStr) {
          return {
            label: "Due Today",
            bgColor: "#F3E8FF",
            color: "#7C3AED",
            showRescheduleInfo: false,
          };
        }
        if (dueDate < now) {
          return {
            label: "Overdue",
            bgColor: "#FEE2E2",
            color: "#DC2626",
            showRescheduleInfo: false,
          };
        }
        return {
          label: "Upcoming",
          bgColor: "#E0F2FE",
          color: "#0284C7",
          showRescheduleInfo: false,
        };
      }
    }
  };

  const statusConfig = getStatusConfig();

  const handleRescheduleSubmit = async (data) => {
    if (!leadId) {
      console.error("Lead ID is required for rescheduling");
      return;
    }

    try {
      // Call the reschedule API (backend combines date+time and updates)
      const res = await rescheduleLeadFollowupApi(leadId, {
        newDate: data.newDate,
        reason: data.rescheduleReason || data.reason,
        priority: data.priority,
        notes: data.notes,
        preferredTime: data.preferredTime,
        duration: data.duration,
      });

      // Update local state with the new data from backend response
      const newFollowupDate = res.data.data.next_followup;
      const newDate = dayjs(newFollowupDate);

      setValue(newDate);

      // Use the latest_reschedule from the response
      if (res.data.data.latest_reschedule) {
        setRescheduleInfo({
          reason: res.data.data.latest_reschedule.reason,
          notes: res.data.data.latest_reschedule.notes,
          time: res.data.data.latest_reschedule.preferred_time,
          rescheduled_at: res.data.data.latest_reschedule.rescheduled_at,
        });
      }

      // Refresh lead data to update UI
      if (refreshLead) {
        refreshLead();
      }

      // Close the modal
      setOpen(false);
    } catch (err) {
      console.error("Failed to reschedule follow-up:", err);
    }
  };

  const handleMarkCompleted = async () => {
    if (!leadId) {
      console.error("Lead ID is required for marking as completed");
      return;
    }

    try {
      await markFollowupCompletedApi(leadId, {
        notes: "",
      });

      // Refresh lead data to update UI
      if (refreshLead) {
        refreshLead();
      }
    } catch (err) {
      console.error("Failed to mark follow-up as completed:", err);
    }
  };

  const handleCancel = () => setOpen(false);

  useEffect(() => {
    setValue(followup ? dayjs(followup) : null);
  }, [followup]);

  // Format datetime consistently using UTC to avoid timezone confusion
  const getFormattedDateTime = () => {
    if (!value) return "No date selected";
    if (rescheduleInfo?.time) {
      return `${value.format("DD MMM YYYY")}, ${rescheduleInfo.time.toLowerCase().replace(' ', '')}`;
    }
    // Display time in local timezone
    return value.format("DD MMM YYYY, hh:mm a");
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              // fontStyle: "italic",
              color: "primary.main",
            }}
          >
            Next Follow Up
          </Typography>
          {statusConfig.label && (
            <Chip
              label={statusConfig.label}
              size="small"
              sx={{
                bgcolor: statusConfig.bgColor,
                color: statusConfig.color,
                fontWeight: 600,
                fontSize: 12,
                height: 28,
                borderRadius: "14px",
              }}
            />
          )}
        </Box>

        {/* Date Display with Calendar Icon */}
        <MotionBox
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            cursor: "pointer",
            mb: statusConfig.showRescheduleInfo ? 2 : 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: (theme) => `${theme.palette.primary.main}1A`,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarMonthIcon
              sx={{ fontSize: 28, color: "primary.main" }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 600,
                color: "#1E293B",
              }}
            >
              {getFormattedDateTime()}
            </Typography>
            {rescheduleInfo && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "primary.main",
                  fontWeight: 500,
                  mt: 0.25,
                }}
              >
                Rescheduled Time
              </Typography>
            )}
          </Box>
        </MotionBox>

        {/* Reschedule Button - Always visible */}
        <Box sx={{ mb: statusConfig.showRescheduleInfo ? 2 : 0, display: "flex", gap: 2, alignItems: "center" }}>
          <Typography
            onClick={() => setOpen(true)}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "primary.main",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all 0.2s",
              display: "inline-block",
              "&:hover": {
                color: "primary.dark",
                textDecoration: "underline",
              },
            }}
          >
            {followupStatus === "completed" ? "↻ Reschedule Again" : "↻ Reschedule"}
          </Typography>

          {/* Mark Done Button - Only show if not completed */}
          {followupStatus !== "completed" && (
            <Typography
              onClick={handleMarkCompleted}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#10B981",
                letterSpacing: 0.5,
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "inline-block",
                "&:hover": {
                  color: "#059669",
                  textDecoration: "underline",
                },
              }}
            >
              ✓ Mark Done
            </Typography>
          )}
        </Box>

        {/* Rescheduled Info Section - only show for rescheduled status */}
        {statusConfig.showRescheduleInfo && rescheduleInfo && (
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "primary.main",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                mb: 2,
                pt: 2,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              Rescheduled Info
            </Typography>

            {/* Reason */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "#94A3B8",
                  mt: 0.25,
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#94A3B8",
                    letterSpacing: "0.3px",
                    textTransform: "uppercase",
                    mb: 0.5,
                  }}
                >
                  Reason
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#374151",
                    fontWeight: 500,
                  }}
                >
                  {rescheduleInfo.reason || "Client requested later time"}
                </Typography>
              </Box>
            </Box>

            {/* Notes */}
            {rescheduleInfo.notes && (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <NotesOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: "#94A3B8",
                    mt: 0.25,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#94A3B8",
                      letterSpacing: "0.3px",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    Notes
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#374151",
                      fontWeight: 400,
                      fontStyle: "italic",
                    }}
                  >
                    "{rescheduleInfo.notes}"
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Reschedule Modal */}
        <RescheduleModal
          open={open}
          onClose={handleCancel}
          task={{
            clientName: "Follow-up",
            taskType: "Next Follow Up",
            dueDate: followup,
            priority: rescheduleInfo?.priority || lead?.priority || "Medium",
            notes: rescheduleInfo?.notes || "",
            reason: rescheduleInfo?.reason || "",
            preferredTime: rescheduleInfo?.time || "",
          }}
          onSubmit={handleRescheduleSubmit}
        />
      </CardContent>
    </MotionCard>
  );
}
